import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuthStore } from '@/core/auth/auth-store'
import { AUTH_BYPASS } from '@/core/auth/dev-bypass'
import { useMyProfile, useHydrateMockFromProfile } from '@/features/perfil/use-my-profile'
import { useAppReady } from '@/shared/hooks/use-app-ready'

// Gate for authenticated routes. Renders nothing until the first auth snapshot
// resolves (avoids a login-screen flash on reload), then redirects or passes
// through. Once authenticated it also resolves the player's `users/{uid}` doc:
// a signed-in account with NO doc is a first run → /onboarding; an existing
// doc hydrates the in-session mock store so stubbed features share the
// persisted identity. The query is cached (one read per session), so nesting
// this gate per-route costs nothing after the first resolution.
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const status = useAuthStore((s) => s.status)
  const isAnonymous = useAuthStore((s) => s.user?.isAnonymous ?? false)
  const profile = useMyProfile()
  const hydrated = useHydrateMockFromProfile(profile.data)
  // The static splash stays up until this gate can actually paint something
  // real (auth resolved AND, when signed in, the profile fetch settled).
  useAppReady(
    AUTH_BYPASS ||
      status === 'unauthenticated' ||
      (status === 'authenticated' && !profile.isPending),
  )
  if (AUTH_BYPASS) return <>{children}</>
  if (status === 'initial') return null
  if (status === 'unauthenticated') return <Navigate to="/login" replace />
  if (profile.isPending) return null
  // Fetch failed (offline / rules): pass through with the mock identity rather
  // than trapping the player on a blank screen — the doc re-resolves next load.
  if (profile.isError) return <>{children}</>
  // An ANONYMOUS session with no profile is not a first run — it is a share-door
  // visitor whose uid was minted silently so Firestore rules would let them read
  // the match. Sending them to /onboarding builds a profile on a throwaway uid
  // and, worse, is where a RETURNING player with a real account ended up when
  // they tapped "Ya tengo cuenta · Entrar": /login bounced them to "/", "/" is
  // protected, and this line finished the trap. /login is the honest
  // destination — they can sign in, and `linkWithCredential` keeps the uid.
  //
  // A full account with no doc IS a first run and still goes to onboarding, and
  // an anonymous session that HAS a doc (the guest who completed onboarding from
  // the share door) passes straight through — that path is deliberate.
  if (profile.data === null) {
    return <Navigate to={isAnonymous ? '/login' : '/onboarding'} replace />
  }
  // Hold one frame until the mock store reflects the persisted identity —
  // children read it non-reactively during render (see useHydrateMockFromProfile).
  if (!hydrated) return null
  return <>{children}</>
}
